import { defineStore } from "pinia";
import type { Line } from "~/stores/line";
import type { PaginatedResponse } from "~~/shared/types/api";

export type FavoriteLine = Pick<Line, "slug" | "name"> & {
  identifier: string | null;
  fromCity: Line["fromCity"] | null;
  toCity: Line["toCity"] | null;
};
type Operation = { line: FavoriteLine; present: boolean; revision: number };
type Account = {
  version: 1;
  cache: FavoriteLine[];
  pending: Record<string, Operation>;
};
const anonymousKey = "nave__favorite_lines";

export const useFavoritesStore = defineStore("favorites", () => {
  const config = useRuntimeConfig();
  const favorites = ref<FavoriteLine[]>([]);
  const syncing = ref(false);
  const error = ref<string | null>(null);
  const suspended = ref(false);
  const account = ref<Account>({ version: 1, cache: [], pending: {} });
  const pendingCount = computed(
    () => Object.keys(account.value.pending).length,
  );
  let identifier: string | null = null;
  let token: string | null = null;
  let generation = 0;
  let revision = 0;
  let controller: AbortController | undefined;
  const key = () =>
    `nave__favorite_lines:v1:${encodeURIComponent(identifier!)}`;

  function persist() {
    try {
      if (identifier) {
        account.value.cache = favorites.value;
        localStorage.setItem(key(), JSON.stringify(account.value));
      } else if (!token) {
        localStorage.setItem(anonymousKey, JSON.stringify(favorites.value));
      }
      return true;
    } catch {
      error.value = "Não foi possível salvar os favoritos neste dispositivo.";
      return false;
    }
  }

  function read<T>(storageKey: string, fallback: T): T {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? "null") ?? fallback;
    } catch {
      return fallback;
    }
  }

  function apply(lines: FavoriteLine[]) {
    const merged = new Map(lines.map((line) => [line.slug, line]));
    for (const [slug, operation] of Object.entries(account.value.pending)) {
      if (operation.present) merged.set(slug, operation.line);
      else merged.delete(slug);
    }
    favorites.value = [...merged.values()];
  }

  function setSession(
    userIdentifier: string | null,
    accessToken: string | null,
  ) {
    const nextIdentifier = accessToken ? userIdentifier : null;
    if (generation && identifier === nextIdentifier && token === accessToken)
      return;
    generation++;
    controller?.abort();
    syncing.value = false;
    suspended.value = false;
    error.value = null;
    identifier = nextIdentifier;
    token = accessToken;
    account.value = { version: 1, cache: [], pending: {} };
    if (!identifier) {
      favorites.value = token ? [] : read<FavoriteLine[]>(anonymousKey, []);
      return;
    }
    const saved = read<Account>(key(), account.value);
    if (saved.version === 1 && Array.isArray(saved.cache) && saved.pending)
      account.value = saved;
    revision = Math.max(
      revision,
      ...Object.values(account.value.pending).map((op) => op.revision),
    );
    const anonymous = read<FavoriteLine[]>(anonymousKey, []);
    for (const line of anonymous) {
      // A saved removal takes precedence even if an interrupted import is repeated.
      if (!account.value.pending[line.slug]) {
        account.value.pending[line.slug] = {
          line,
          present: true,
          revision: ++revision,
        };
      }
    }
    apply(account.value.cache);
    if (persist()) {
      try {
        localStorage.removeItem(anonymousKey);
      } catch {
        /* Import can safely repeat. */
      }
    }
    void sync();
  }

  async function sync() {
    if (!identifier || !token || syncing.value || suspended.value) return;
    const epoch = generation;
    const active = () => epoch === generation;
    controller = new AbortController();
    const options = {
      baseURL: config.public.apiUrl,
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
      retry: 0,
    };
    syncing.value = true;
    error.value = null;
    const failed = new Set<string>();
    const handleError = (cause: unknown) => {
      const status = (cause as { statusCode?: number }).statusCode;
      if (status === 401 || status === 403) {
        suspended.value = true;
        error.value = "Entre novamente para sincronizar seus favoritos.";
      } else {
        error.value =
          "Não foi possível sincronizar. Suas alterações continuam salvas neste dispositivo.";
      }
      return status;
    };
    try {
      const remote: FavoriteLine[] = [];
      let page = 1;
      while (true) {
        const result = await $fetch<PaginatedResponse<FavoriteLine>>(
          "/users/me/favorites/lines",
          {
            ...options,
            query: { page, perPage: 100 },
          },
        );
        if (!active()) return;
        remote.push(...result.data);
        if (!result.pagination.hasNext) break;
        page++;
      }
      apply(remote);
      persist();
      while (active() && !suspended.value) {
        const entry = Object.entries(account.value.pending).find(
          ([slug]) => !failed.has(slug),
        );
        if (!entry) break;
        const [slug, operation] = entry;
        try {
          await $fetch(
            `/users/me/favorites/lines/${encodeURIComponent(slug)}`,
            {
              ...options,
              method: operation.present ? "POST" : "DELETE",
            },
          );
          if (!active()) return;
          if (account.value.pending[slug]?.revision === operation.revision)
            delete account.value.pending[slug];
        } catch (cause) {
          if (!active()) return;
          const status = handleError(cause);
          if (status === 404 && operation.present) {
            error.value = `A linha ${operation.line.name} não está mais disponível.`;
            if (account.value.pending[slug]?.revision === operation.revision) {
              delete account.value.pending[slug];
              favorites.value = favorites.value.filter(
                (line) => line.slug !== slug,
              );
            }
          } else failed.add(slug);
        }
        persist();
      }
    } catch (cause) {
      if (active()) handleError(cause);
    } finally {
      if (active()) {
        persist();
        syncing.value = false;
      }
    }
  }

  const isFavorite = (slug: string) =>
    favorites.value.some((line) => line.slug === slug);
  function change(line: FavoriteLine, present: boolean) {
    if (token && !identifier) {
      error.value = "Aguarde a identificação da conta para salvar favoritos.";
      return;
    }
    favorites.value = favorites.value.filter((item) => item.slug !== line.slug);
    if (present) favorites.value.push(line);
    if (identifier)
      account.value.pending[line.slug] = {
        line,
        present,
        revision: ++revision,
      };
    persist();
    void sync();
  }
  const addFavorite = (line: FavoriteLine) => {
    if (!isFavorite(line.slug)) change(line, true);
  };
  const removeFavorite = (slug: string) => {
    const line = favorites.value.find((item) => item.slug === slug);
    if (line) change(line, false);
  };
  const toggleFavorite = (line: FavoriteLine) =>
    change(line, !isFavorite(line.slug));

  return {
    favorites,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    syncing,
    error,
    pendingCount,
    suspended,
    sync,
    setSession,
  };
});
